import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { StatusPill, Modal, Select, Area, Text } from "@/components/admin/AdminUI";
import { ApprovePanel, AdminMessages, ArchiveControl } from "@/components/admin/RequestWorkflow";
import { DeleteRequestButton } from "@/components/admin/DeleteRequestButton";
import { toast } from "sonner";
import { Eye, EyeOff, MessageCircle } from "lucide-react";

const STATUSES = ["New Request", "Reviewing", "Need More Information", "Quoted", "Accepted", "Approved", "Paid", "Declined", "Completed"];
function HiddenBadge({ testid }) {
  return (
    <span data-testid={testid} className="ml-2 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-kys-charcoal/10 px-2 py-0.5 text-xs font-medium text-kys-charcoal">
      <EyeOff className="h-3 w-3" />Hidden by customer
    </span>
  );
}

const MANUAL = ["New Request", "Reviewing", "Need More Information", "Quoted", "Accepted", "Declined", "Completed"];

export default function AdminRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [edit, setEdit] = useState({ status: "", admin_notes: "", quoted_price: "" });

  const [params, setParams] = useSearchParams();
  const ARCHIVED = "__archived";
  const load = () => api.get("/admin/custom-requests", { params: filter === ARCHIVED ? { archived: true } : filter ? { status: filter } : {} }).then((r) => {
    setItems(r.data);
    const open = params.get("open");
    const hit = open && r.data.find((x) => x.request_number === open);
    if (hit) { openReq(hit); setParams({}, { replace: true }); }
  });
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const openReq = (r) => { setActive(r); setEdit({ status: r.status, admin_notes: r.admin_notes || "", quoted_price: r.quoted_price ?? "" }); };

  const save = async () => {
    try {
      const lockedPrice = ["Approved", "Paid"].includes(active.status) || active.locked;
      const { data } = await api.patch(`/admin/custom-requests/${active.id}`, { status: edit.status, admin_notes: edit.admin_notes, quoted_price: lockedPrice || edit.quoted_price === "" ? null : Number(edit.quoted_price) });
      if (data?.fabric_warning) toast.warning(data.fabric_warning, { duration: 10000 });
      else toast.success("Updated");
      setActive(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl text-kys-ink">Custom Quote Requests</h1>
      <p className="text-sm text-kys-muted">Review requests and set pricing manually. No auto-quoting.</p>

      <div className="no-scrollbar mt-4 flex gap-2 overflow-x-auto">
        <button onClick={() => setFilter("")} className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs ${!filter ? "border-kys-ink bg-kys-ink text-white" : "border-kys-blush-border bg-white"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs ${filter === s ? "border-kys-ink bg-kys-ink text-white" : "border-kys-blush-border bg-white"}`}>{s}</button>
        ))}
        <button data-testid="filter-archived" onClick={() => setFilter(ARCHIVED)} className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs ${filter === ARCHIVED ? "border-kys-ink bg-kys-ink text-white" : "border-dashed border-kys-charcoal/40 bg-white"}`}>Archived</button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-kys-blush-border bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-kys-blush-border bg-kys-blush text-left text-xs uppercase tracking-wide text-kys-muted">
            <tr><th className="p-3">Request #</th><th className="p-3">Customer</th><th className="p-3">Pieces</th><th className="p-3">Status</th><th className="p-3">Date</th><th className="sticky right-0 bg-kys-blush p-3 text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-kys-blush-border">
            {items.map((r) => (
              <tr key={r.id} data-testid={`request-row-${r.request_number}`}>
                <td className="p-3 font-mono text-xs">{r.request_number}</td>
                <td className="p-3">{r.customer?.name}<br /><span className="text-xs text-kys-muted">{r.customer?.email}</span></td>
                <td className="p-3">{r.items?.length}</td>
                <td className="p-3"><StatusPill status={r.status} />{r.unread_by_admin && <span data-testid={`unread-${r.request_number}`} className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-kys-accent"><MessageCircle className="h-3.5 w-3.5" />New</span>}{r.hidden_by_customer && <HiddenBadge testid={`hidden-badge-${r.request_number}`} />}</td>
                <td className="p-3 text-xs text-kys-muted">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="sticky right-0 bg-white p-1.5 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openReq(r)} data-testid={`view-${r.request_number}`} aria-label="View request" className="flex h-10 w-10 items-center justify-center rounded-full text-kys-charcoal transition-colors hover:bg-kys-blush hover:text-kys-accent"><Eye className="h-5 w-5" /></button>
                    <DeleteRequestButton request={r} onDeleted={load} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="p-8 text-center text-sm text-kys-muted">No requests yet.</p>}
      </div>

      <Modal open={!!active} onClose={() => setActive(null)} title={active?.request_number} wide>
        {active && (
          <div className="space-y-4">
            {(active.hidden_by_customer || active.admin_archived) && (
              <div data-testid="hidden-notice" className="flex flex-wrap items-center gap-2 rounded border border-dashed border-kys-charcoal/30 bg-kys-cream p-3 text-xs text-kys-charcoal">
                <HiddenBadge testid="hidden-badge-modal" />
                Customer removed this from their My Custom Orders view{active.hidden_at ? ` on ${new Date(active.hidden_at).toLocaleDateString()}` : ""}. Info only — nothing else changed.
                {active.admin_archived && <span data-testid="archived-note" className="w-full font-medium">Archived from your list{active.admin_archived_at ? ` on ${new Date(active.admin_archived_at).toLocaleDateString()}` : ""}.</span>}
                <div className="ml-auto"><ArchiveControl request={active} onDone={() => { setActive(null); load(); }} /></div>
              </div>
            )}
            <div className="rounded border border-kys-blush-border p-3 text-sm">
              <p className="font-medium text-kys-ink">{active.customer?.name}</p>
              <p className="text-kys-muted">{active.customer?.email} · {active.customer?.phone || "no phone"}</p>
              <p className="mt-1 text-xs text-kys-muted">Needed by: {active.needed_by || "Flexible"}</p>
            </div>

            {active.items?.map((it, i) => (
              <div key={i} className="flex gap-3 rounded border border-kys-blush-border p-3">
                <div className="h-24 w-20 shrink-0 overflow-hidden rounded bg-kys-blush">{(it.mockup_image || it.fabric?.images?.[0] || it.style_image) && <img src={fileUrl(it.mockup_image || it.fabric?.images?.[0] || it.style_image)} alt="" className="h-full w-full object-cover" />}</div>
                <div className="text-sm">
                  <p className="font-medium text-kys-ink">{it.style_name} <span className="text-xs text-kys-muted">· {it.category_name}</span></p>
                  <p className="text-xs text-kys-charcoal">Fabric: {it.fabric?.fabric_name || "To discuss"}</p>
                  <p className="text-xs text-kys-charcoal">Add-ons: {it.addons?.length ? it.addons.map((a) => a.name).join(", ") : "None"}</p>
                  {it.measurements && Object.keys(it.measurements).length > 0 && <p className="text-xs text-kys-charcoal">Measurements: {Object.entries(it.measurements).filter(([, v]) => v).map(([k, v]) => `${k} ${v}"`).join(", ")}</p>}
                  {it.notes && <p className="text-xs text-kys-muted">Note: {it.notes}</p>}
                </div>
              </div>
            ))}

            {active.general_notes && <div className="rounded bg-kys-blush p-3 text-sm"><span className="text-kys-muted">Notes:</span> {active.general_notes}</div>}
            <div data-testid="mockup-ack-status" className="rounded border border-kys-blush-border p-3 text-xs text-kys-charcoal">
              <span className="font-semibold">Mockup acknowledgment: </span>
              {active.mockup_acknowledgment?.accepted
                ? <>Accepted on {new Date(active.mockup_acknowledgment.accepted_at).toLocaleString()}</>
                : <span className="text-kys-muted">Not recorded (submitted before this was required)</span>}
            </div>
            {active.inspiration_images?.length > 0 && (
              <div><p className="mb-2 text-xs font-medium text-kys-charcoal">Inspiration photos</p><div className="flex flex-wrap gap-2">{active.inspiration_images.map((u, i) => <img key={i} src={fileUrl(u)} alt="" className="h-20 w-16 rounded border border-kys-blush-border object-cover" />)}</div></div>
            )}

            {active.status !== "Declined" && <ApprovePanel request={active} onDone={(r) => { setActive(r); setEdit({ status: r.status, admin_notes: r.admin_notes || "", quoted_price: r.quoted_price ?? "" }); load(); }} />}
            <AdminMessages request={active} />

            <div className="border-t border-kys-blush-border pt-4">
              <div className="grid grid-cols-2 gap-3">
                <Select label="Status" value={edit.status} onChange={(v) => setEdit({ ...edit, status: v })}
                  options={(active.locked ? ["Paid", "Completed"] : [...MANUAL, ...(active.status === "Approved" ? ["Approved"] : [])]).map((s) => ({ value: s, label: s }))} testid="req-status" />
                {["Approved", "Paid"].includes(active.status) || active.locked ? <div /> :
                  <Text label="Quoted price (USD)" type="number" value={edit.quoted_price} onChange={(v) => setEdit({ ...edit, quoted_price: v })} testid="req-price" />}
              </div>
              <div className="mt-3 rounded border border-dashed border-kys-charcoal/30 bg-kys-cream p-3">
                <Area label="Private admin notes (never shown to customer)" value={edit.admin_notes} onChange={(v) => setEdit({ ...edit, admin_notes: v })} testid="req-private-notes" />
              </div>
              <button data-testid="save-request" onClick={save} className="mt-3 w-full rounded-full bg-kys-ink py-3 text-sm font-semibold text-white hover:bg-kys-accent">Save</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
